import { clamp01 } from "atlas_forecasting_app/lib/math"

const SCIENTIFIC_NOTATION_LOWER_BOUND = 1e-4
const SCIENTIFIC_NOTATION_UPPER_BOUND = 1e10

function parseDateValue(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date
}

export function formatDateTime(value) {
  const date = parseDateValue(value)
  if (!date) return value ? String(value) : ""
  return date.toLocaleString()
}

export function formatDate(value) {
  const date = parseDateValue(value)
  if (!date) return value ? String(value) : ""
  return date.toLocaleDateString()
}

export function formatTime(value) {
  const date = parseDateValue(value)
  if (!date) return value ? String(value) : ""
  return date.toLocaleTimeString()
}

export function formatCurrency(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return "—"
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function formatCurrencyFromCents(cents) {
  const n = Number(cents)
  if (!Number.isFinite(n)) return "—"
  return formatCurrency(n / 100)
}

export function formatSignedCurrencyFromCents(cents) {
  const n = Number(cents)
  if (!Number.isFinite(n)) return "—"

  const formatted = formatCurrencyFromCents(Math.abs(n))
  if (n > 0) return `+${formatted}`
  if (n < 0) return `-${formatted}`
  return formatted
}

export function formatPercent(p, digits = 1) {
  const n = clamp01(Number(p))
  if (n == null) return "—"
  return `${(n * 100).toFixed(digits)}%`
}

function normalizedFractionDigits(value) {
  const digits = Number(value)
  if (!Number.isFinite(digits)) return 0
  return Math.max(0, Math.floor(digits))
}

function normalizedDisplayValue(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return n
  return Number(n.toPrecision(13))
}

function shouldUseScientificNotation(value) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n) || n === 0) return false
  const absValue = Math.abs(n)
  return absValue < SCIENTIFIC_NOTATION_LOWER_BOUND || absValue > SCIENTIFIC_NOTATION_UPPER_BOUND
}

function trimScientificNotation(text) {
  return text.replace(/(\.\d*?[1-9])0+e/, "$1e").replace(/\.0+e/, "e").replace(/e\+/, "e")
}

function formatScientificNumber(value, significantDigits) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n)) return "—"

  const digits = Math.max(1, Math.floor(significantDigits))

  try {
    return trimScientificNotation(n.toExponential(digits - 1))
  } catch (_error) {
    return String(n)
  }
}

function significantDigitsFromFixedPrecision(value, fractionDigits) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n) || n === 0) return 1

  const absValue = Math.abs(n)
  const orderOfMagnitude = Math.floor(Math.log10(absValue))
  return Math.max(1, fractionDigits + orderOfMagnitude + 1)
}

function fixedString(value, fractionDigits) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n)) return null

  try {
    return n.toFixed(fractionDigits)
  } catch (_error) {
    return null
  }
}

function valuesRemainDistinct(values, fractionDigits) {
  const seen = new Set()

  for (const value of values) {
    const text = fixedString(value, fractionDigits)
    if (text == null) return true
    if (seen.has(text)) return false
    seen.add(text)
  }

  return true
}

function valuesRemainClose(values, fractionDigits, referenceGap) {
  if (!Number.isFinite(referenceGap) || referenceGap <= 0) return true

  const maxError = referenceGap / 10
  for (const value of values) {
    const text = fixedString(value, fractionDigits)
    if (text == null) return true
    if (Math.abs(Number(text) - value) > maxError) return false
  }

  return true
}

function exactFractionDigitsForValue(value) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n) || Number.isInteger(n)) return 0

  let digits = 0
  while (true) {
    const text = fixedString(n, digits)
    if (text == null) return Math.max(0, digits - 1)
    if (Number(text) === n) return digits
    digits += 1
  }
}

export function adaptiveFractionDigits(values, minimumFractionDigits = 0) {
  const minimum = normalizedFractionDigits(minimumFractionDigits)
  const finiteValues = Array.from(
    new Set(
      Array.isArray(values)
        ? values
            .map((value) => normalizedDisplayValue(value))
            .filter((value) => Number.isFinite(value))
        : []
    )
  ).sort((a, b) => a - b)

  if (finiteValues.length === 0) return minimum
  if (finiteValues.length === 1) return Math.max(minimum, exactFractionDigitsForValue(finiteValues[0]))

  let minGap = Infinity
  for (let idx = 1; idx < finiteValues.length; idx += 1) {
    const gap = normalizedDisplayValue(finiteValues[idx] - finiteValues[idx - 1])
    if (gap > 0 && gap < minGap) minGap = gap
  }

  const smallestAbsNonZero = finiteValues.reduce((smallest, value) => {
    const absValue = Math.abs(value)
    if (absValue === 0) return smallest
    if (!Number.isFinite(smallest) || absValue < smallest) return absValue
    return smallest
  }, Infinity)

  let digits = minimum
  if (Number.isFinite(minGap) && minGap < 1) {
    digits = Math.max(digits, Math.max(0, -Math.floor(Math.log10(minGap))))
  }
  if (Number.isFinite(smallestAbsNonZero) && smallestAbsNonZero < 1) {
    digits = Math.max(digits, Math.max(0, Math.ceil(-Math.log10(smallestAbsNonZero))))
  }

  while (!valuesRemainDistinct(finiteValues, digits) || !valuesRemainClose(finiteValues, digits, minGap)) digits += 1

  return digits
}

export function formatFixedNumber(value, fractionDigits = 0) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n)) return "—"

  const digits = normalizedFractionDigits(fractionDigits)
  if (shouldUseScientificNotation(n)) {
    return formatScientificNumber(n, significantDigitsFromFixedPrecision(n, digits))
  }

  try {
    return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
  } catch (_error) {
    try {
      return n.toFixed(digits)
    } catch (_fixedError) {
      return String(n)
    }
  }
}

export function formatNumber(value, maximumFractionDigits = 2) {
  const n = normalizedDisplayValue(value)
  if (!Number.isFinite(n)) return "—"
  const digits = normalizedFractionDigits(maximumFractionDigits)
  if (shouldUseScientificNotation(n)) return formatScientificNumber(n, digits + 1)
  return n.toLocaleString(undefined, { maximumFractionDigits: digits })
}
